import {
  Box,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  CancelIcon,
  CheckIcon,
  CogIcon,
  InformationOutlineIcon,
} from '@atgfr8/icons';
import { BasicModal, Button, SelectInput, ToastType } from '@components';
import {
  flagImgs,
  getOptionsByRole,
  infoMsgObj,
} from '@components/FlagPopup/constants';
import { LoadComment } from '@components/FlagPopup/LoadComment';
import { FeatureFlags } from '@constants/featureFlags';
import { createAppStore } from '@store/createAppStore';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { setSidebarDataStore } from '@store/Global/store';
import {
  addFlagCommentV4,
  CommentViewModel,
  deleteFlag,
  deleteFlagComment,
  disputeFlag,
  FlagType,
  orderStore,
  updateFlag,
  updateOrderState,
} from '@store/orders';
import {
  getAvailableTransitions,
  getInitialStates,
  getStateDefinition,
  initializeOrderFlagStates,
  orderFlagStatesStore,
} from '@store/orderFlagStates/store';
import { userStore } from '@store/user';
import { FlagViewModel } from '@typeDefinitions/flagTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast, isAdmin } from '@utils/utils';
import { isEmpty, uniqueId } from 'lodash';
import { createSignal, onMount } from 'solid-js';
import * as yup from 'yup';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

type InitialFormVals = {
  message: string;
  selectedFlag: string;
  emailId: string;
  description: string;
  currentState: string;
};

const schema = yup.object().shape({
  message: yup
    .string()
    .required('Comment with length greater than 0 is required to Approve.'),
  emailId: yup
    .string()
    .email('Invalid email')
    .required('Email is required')
    .max(40, 'Email must be at most 40 characters'),
  description: yup
    .string()
    .required('Description is required')
    .max(500, 'Description must be at most 500 characters'),
});

// eslint-disable-next-line complexity
export const NeedsApprovalModal = (props: {
  onClose: () => void;
  modalId: string;
  flagData: FlagViewModel;
}) => {
  const initialCommentCount = Boolean(props.flagData.commentCount)
    ? props.flagData.commentCount
    : 0;

  const debugId = uniqueId('orderFormValues_');
  const [formValues, setFormValues] = createAppStore<InitialFormVals>(
    {
      message: '',
      selectedFlag: props.flagData.flagType,
      emailId: '',
      description: props.flagData.description ?? '',
      currentState: props.flagData.currentState ?? '',
    },
    debugId,
  );

  const [errorValues, setErrorValues] = createAppStore(
    {
      message: '',
      emailId: '',
      description: '',
    },
    uniqueId('orderErrorValues_'),
  );

  const [isLoading, setIsLoading] = createSignal(false);

  const validateField = (field: string, value: string) => {
    try {
      schema.validateSyncAt(field, { [field]: value });
      setErrorValues({ ...errorValues, [field]: '' });
    } catch (e: unknown) {
      if (e instanceof yup.ValidationError) {
        setErrorValues({ ...errorValues, [field]: e.message });
      }
    }
  };

  const handleDeleteFlagComment = async (id: number) => {
    await deleteFlagComment(
      id,
      CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagDeleteComment,
    );
    const comments = [...(props.flagData.comments ?? [])].filter(
      (comment) => comment.id !== id,
    );
    updateOrderState({
      needsApprovalFlag: {
        ...props.flagData,
        comments,
        commentCount: comments.length,
      },
    });
  };

  const handleAddFlagComment = async (comment: string) => {
    const resp = (await addFlagCommentV4(
      props.flagData.id ?? 0,
      comment.trim(),
      CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagAddComment,
    )) as unknown as CommentViewModel;
    const comments = [...(props.flagData.comments ?? [])];
    comments.push(resp as unknown as CommentViewModel);
    updateOrderState({
      needsApprovalFlag: {
        ...props.flagData,
        comments,
        commentCount: comments.length,
      },
    });
    setFormValues('message', '');
  };

  const handleClose = () => {
    setFormValues('message', '');
    setErrorValues({
      message: '',
      emailId: '',
      description: '',
    });
    props.onClose();
  };

  const handleDisputed = async () => {
    const trimmedMessage = formValues.message.trim();
    if (trimmedMessage) {
      await handleAddFlagComment(trimmedMessage);
      await persistSubStatusIfChanged();
      await disputeFlag(
        props.flagData.id as number,
        userStore.user.name,
        CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagDispute,
      )
        .then((res) => {
          const comments = [...(props.flagData.comments ?? [])];
          comments.unshift({
            timestamp: res?.timestamp,
            comment: `${userStore.user.name} has Disputed the Carrier Invoice.`,
            name: userStore.user.name,
            me: true, // pushed in V3
          } as unknown as CommentViewModel);

          //ATG.App.trigger(ATG.Event.APPROVAL_COMMENT, resp.data);
          if (res) {
            const disputedOrderFlags = [
              ...(props.flagData.disputedOrderFlags ?? []),
            ];
            disputedOrderFlags.push({
              clearedBy: undefined,
              clearedDate: undefined,
              disputedDate:
                res.timestamp?.toString() ?? new Date().toISOString(),
              flagId: props.flagData.id as number,
              // The guid below is a temprorary fix because the BE is not giving us the guid of the
              // new needs approval flag's disputed flag. If the operation type changes to anything
              // other than "None" that op will fail because this guid is here just so that we can
              // pass validation.
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              id: '00000000-0000-0000-0000-000000000000',
              operationType: 'None',
            });

            updateOrderState({
              needsApprovalFlag: {
                ...props.flagData,
                comments,
                disputedOrderFlags,
              },
            });
          }
        })
        .catch((error: Error) => {
          handleToast(ToastType.Error, error.message);
        });
      handleClose();
    }
  };

  const handleFlagDelete = async (comment?: string) => {
    await deleteFlag(
      props.flagData.id as number,
      comment,
      CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagDelete,
    );
    const comments = [...(props.flagData.comments ?? [])];
    if (comment && comment.trim() !== '') {
      comments.push({
        comment: comment,
        timestamp: new Date().toISOString(),
        name: userStore.user.name,
        me: true,
      } as unknown as CommentViewModel);
    }
    updateOrderState({
      needsApprovalFlag: {
        ...props.flagData,
        cleared: true,
        comments: comments,
        commentCount: comments.length,
      },
    });
  };

  const handleOkToProcess = async () => {
    setIsLoading(true);
    try {
      const trimmedMessage = formValues.message.trim();
      if (trimmedMessage !== '') {
        await handleFlagDelete(trimmedMessage);
        handleClose();
      } else {
        validateField('message', formValues.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleStillWorking = async () => {
    setIsLoading(true);
    try {
      if (formValues.message.trim() !== '') {
        await handleAddFlagComment(formValues.message);
        setFormValues('message', '');
      }
      await persistSubStatusIfChanged();
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const isNew = () => {
    if (props.flagData.id == null) return true;
    return props.flagData.id <= 0;
  };

  // Ok To Process/Still Working/Dispute never render the 'Update Flag' button for an
  // existing 'Needs Approval' flag, so the sub-status pick has to ride along with them.
  const persistSubStatusIfChanged = async () => {
    if (
      showSubStatus() &&
      formValues.currentState &&
      formValues.currentState !== props.flagData.currentState
    ) {
      const currentFlag = orderStore.order.needsApprovalFlag ?? props.flagData;
      const updated = await updateFlag(
        {
          ...currentFlag,
          currentState: formValues.currentState,
          mode: 'Queue',
        },
        CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagUpdate,
      );
      const newCurrentState = updated.currentState ?? formValues.currentState;
      updateOrderState({
        needsApprovalFlag: {
          ...currentFlag,
          currentState: newCurrentState,
        },
      });
      setSidebarDataStore('approvalsData', 'allApprovals', (prev) => {
        const indexOfApproval = prev.findIndex(
          (item) => item.id === props.flagData.id,
        );
        if (indexOfApproval === -1) {
          return prev;
        }
        const updatedApprovals = [...prev];
        updatedApprovals[indexOfApproval] = {
          ...updatedApprovals[indexOfApproval],
          currentState: newCurrentState,
        };
        return updatedApprovals;
      });
    }
  };

  const handleUpdateCreateFlag = async () => {
    if (formValues.selectedFlag === 'Missing Paperwork') {
      if (formValues.emailId.trim() == '') {
        validateField('emailId', formValues.emailId);
        // ATG.App.message('Email Id is required', "Error"); //add this error to error object
        return false;
      }
      if (formValues.description.trim() != '') {
        if (formValues.description.indexOf(';') != -1) {
          setErrorValues({
            ...errorValues,
            description:
              'Semicolons are now allowed in either the Email Id and Description fields',
          });
          return false;
        } else if (formValues.emailId.indexOf(';') != -1) {
          setErrorValues({
            ...errorValues,
            emailId:
              'Semicolons are now allowed in either the Email Id and Description fields',
          });
          return false;
        }
        setFormValues('emailId', formValues.emailId.trim());
        setFormValues('description', formValues.description.trim());
      }
    } else {
      setFormValues('emailId', '');
    }
    //
    const payload = {
      ...props.flagData,
      flagType: formValues.selectedFlag as FlagType,
      description: formValues.description,
      emailId: formValues.emailId,
      me: false,
      mode: 'Queue',
      ...(showSubStatus() && formValues.currentState
        ? { currentState: formValues.currentState }
        : {}),
    };

    await updateFlag(
      payload,
      CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderFlagUpdate,
    );
    // TODO to handle updating flag id needed

    handleClose();
  };

  const handleCloseOrDelete = async () => {
    if (!isEmpty(props.flagData.id) && (props.flagData.id ?? 0) > 0) {
      updateOrderState({
        needsApprovalFlag: { ...props.flagData, cleared: true, comments: [] },
      });
      await handleFlagDelete().then(() => {
        handleToast(ToastType.Success, 'Flag has been removed.');
      });
    }
    handleClose();
  };

  const isDisputable = () => {
    const disputedOrderFlags =
      orderStore.order.needsApprovalFlag?.disputedOrderFlags ?? [];

    if (props.flagData.cleared ?? false) {
      return false;
    } else if (disputedOrderFlags.length === 0) {
      return true;
    }

    return disputedOrderFlags.some((flag) => {
      return flag.clearedDate !== undefined && flag.clearedDate !== null;
    });
  };

  const shouldDisableButton = () => {
    const isMessageShort = formValues.message.trim().length < 3;
    let hasNoComments = false;
    if (props.flagData.commentCount !== undefined) {
      hasNoComments = props.flagData.commentCount <= (initialCommentCount || 0);
    }
    return isMessageShort && hasNoComments;
  };

  const isLTL = () => orderStore.order.loads[0].mode === 'LTL';

  const isOrderFlagStateMachineEnabled = isFeatureFlagEnabled(
    FeatureFlags.feature_tms_orderflag_state_machine,
  );
  const isPendingApprovalsV2Enabled = isFeatureFlagEnabled(
    FeatureFlags.PendingApprovalsV2,
  );

  onMount(async () => {
    if (orderFlagStatesStore.loaded || orderFlagStatesStore.isLoading) return;
    await initializeOrderFlagStates();
  });

  const subStatusDefinition = () => getStateDefinition('Needs Approval', 'LTL');

  const subStatusOptions = () => {
    const states = props.flagData.currentState
      ? getAvailableTransitions(
          'Needs Approval',
          'LTL',
          props.flagData.currentState,
        )
      : getInitialStates('Needs Approval', 'LTL');
    const withCurrent = props.flagData.currentState
      ? [props.flagData.currentState, ...states]
      : states;
    return [...new Set(withCurrent)].map((state) => ({
      value: state,
      label: state,
    }));
  };

  // Rollout is documented as requiring only PendingApprovalsV2; keep both flags in lockstep
  // so enabling the documented flag never leaves this control half-enabled.
  const showSubStatus = () =>
    isPendingApprovalsV2Enabled() &&
    isOrderFlagStateMachineEnabled() &&
    formValues.selectedFlag === 'Needs Approval' &&
    isLTL() &&
    Boolean(subStatusDefinition());

  return (
    <BasicModal
      id={props.modalId}
      title="Needs Approval"
      width={'1000px'}
      onClose={handleClose}
      footer={false}
    >
      <>
        <Grid data-debug-id={debugId}>
          <div class="bg-blue-50 p-2 text-xs flex items-center border-2 border-[#468DB5]">
            <InformationOutlineIcon size={55} style={{ color: '#468DB5' }} />
            <span
              class="text-lg ml-3"
              innerHTML={infoMsgObj[formValues.selectedFlag]}
            />
          </div>
          {Boolean(props.flagData.editable) &&
            !['Billing Note', 'Billing Hold'].includes(
              formValues.selectedFlag,
            ) && (
              <Grid container class="my-3">
                <Grid item size={11} spacing={4} class="align-items">
                  <SelectInput
                    label="Queue"
                    menuItems={getOptionsByRole(isAdmin())}
                    onChange={(value) => setFormValues('selectedFlag', value)}
                    value={formValues.selectedFlag}
                    dataTestId="needs-approval-modal-queue"
                  />
                </Grid>
                <Grid item size={1} class="align-items justify-center flex">
                  <img src={flagImgs[formValues.selectedFlag]} alt="alt flag" />
                </Grid>
              </Grid>
            )}
          {showSubStatus() && (
            <Grid container class="my-3">
              <Grid item size={11} spacing={4} class="align-items">
                <SelectInput
                  label="Sub-Status"
                  menuItems={subStatusOptions()}
                  onChange={(value) => setFormValues('currentState', value)}
                  value={formValues.currentState}
                  dataTestId="needs-approval-modal-sub-status"
                />
              </Grid>
            </Grid>
          )}
          {formValues.selectedFlag === FlagType.MissingPaperwork ? (
            <Box class="mb-3!">
              <TextField
                type="text"
                label="Email ID"
                size="md"
                placeholder="Add an email address"
                value={formValues.emailId}
                class="w-full"
                onChange={(emailId: string) => {
                  setFormValues('emailId', emailId);
                }}
                on:blur={() => validateField('emailId', formValues.emailId)}
                error={errorValues.emailId}
                dataTestId="email-id-textInput"
              />
            </Box>
          ) : undefined}
          {Boolean(props.flagData.editable) ||
          formValues.selectedFlag == 'Billing Note' ? (
            <TextField
              multiline
              label="Description"
              placeholder=""
              rows={1}
              value={formValues.description}
              onChange={(value: string) => {
                setFormValues('description', value);
              }}
              error={errorValues.description}
              disabled={
                !isAdmin() && props.flagData.flagType == 'Get Paperwork'
              }
              on:blur={() =>
                validateField('description', formValues.description)
              }
              maxLength={4000}
              dataTestId="description-textAreaField"
            />
          ) : (
            <div class="my-3">
              <Typography
                class="text-black text-xl! font-medium!"
                variant="h6"
                component="h2"
                sxProps={{ marginBottom: '10px' }}
              >
                Description{' '}
              </Typography>
              {props.flagData.description}
            </div>
          )}
        </Grid>
        {Boolean(props.flagData.commentable) && !isNew() && (
          <Box mt="18px">
            <Box>
              <Typography
                class="text-black text-xl! font-medium!  leading-8! tracking-[0.15px]!"
                variant="h6"
                component="h2"
                sxProps={{ marginBottom: '10px' }}
              >
                Message History
              </Typography>
              {formValues.selectedFlag === 'Needs Approval' && isLTL() && (
                <p class="color-[#4863A0] mb-3">
                  To Dispute an invoice, please add a reason for the dispute
                  below. Then click on the "Dispute" button at the bottom.
                </p>
              )}
              <TextField
                value={formValues.message}
                size="md"
                label="Add a Comment"
                class="w-full"
                onChange={(value: string) => {
                  setFormValues('message', value);
                  validateField('message', formValues.message);
                }}
                maxLength={2000}
                onKeyDown={async (e: KeyboardEvent) => {
                  if (
                    e.key === 'Enter' &&
                    formValues.message.trim() !== '' &&
                    !isLoading()
                  ) {
                    e.preventDefault();
                    setIsLoading(true);
                    await handleAddFlagComment(formValues.message);
                  }
                }}
                disabled={
                  formValues.selectedFlag === 'Get Paperwork' && !isAdmin()
                }
                on:blur={() => validateField('message', formValues.message)}
                error={errorValues.message}
                dataTestId="add-a-comment-textInput"
              />
            </Box>
            <Box maxHeight="250px" overflow="auto" class="my-4!">
              <Stack direction="column">
                {props.flagData.comments?.toReversed().map((comment) => {
                  if (!Boolean(comment)) {
                    return <></>;
                  }

                  return (
                    <LoadComment
                      comment={comment}
                      onDelete={() => handleDeleteFlagComment(comment.id)}
                    />
                  );
                })}
              </Stack>
            </Box>
          </Box>
        )}
        <Box class="flex justify-end mt-14" gap={3}>
          {formValues.selectedFlag === 'Needs Approval' && !isNew() ? (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={handleOkToProcess}
                disabled={isLoading()}
                label="Ok To Process"
                startIcon={
                  <CheckIcon size={20} style={{ 'margin-right': '5px' }} />
                }
                dataTestId={formatDataTestId('Ok To Process', 'button')}
              />
              <Button
                variant="contained"
                color="error"
                label="Still Working"
                onClick={handleStillWorking}
                disabled={isLoading()}
                startIcon={
                  <CogIcon size={20} style={{ 'margin-right': '5px' }} />
                }
                dataTestId={formatDataTestId('Still Working', 'button')}
              />
              {isLTL() ? (
                <Button
                  variant="contained"
                  color="warning"
                  disabled={!isDisputable() || shouldDisableButton()}
                  onClick={handleDisputed}
                  title={`${!isDisputable()} - ${shouldDisableButton()}`}
                  label="Dispute"
                  startIcon={
                    <CancelIcon size={20} style={{ 'margin-right': '5px' }} />
                  }
                  dataTestId={formatDataTestId('Dispute', 'button')}
                />
              ) : undefined}
            </>
          ) : null}
          {formValues.selectedFlag !== 'Needs Approval' ||
          ['Billing Note', 'Billing Hold'].includes(formValues.selectedFlag) ? (
            <>
              <Button
                variant="contained"
                onClick={handleUpdateCreateFlag}
                label={
                  props.flagData.id !== undefined ||
                  ['Billing Note', 'Billing Hold'].includes(
                    formValues.selectedFlag,
                  ) ||
                  isNew()
                    ? 'Update Flag'
                    : 'Create Flag'
                }
                dataTestId={formatDataTestId(
                  'Update Flag Create Flag',
                  'button',
                )}
              />
              {(Boolean(props.flagData.editable) && !isNew()) ||
                (['Billing Note', 'Billing Hold'].includes(
                  formValues.selectedFlag,
                ) && (
                  <Button
                    onClick={handleCloseOrDelete}
                    variant="contained"
                    color="error"
                    label={
                      !Boolean(props.flagData.commentable)
                        ? 'Remove Flag'
                        : 'Cancel'
                    }
                    dataTestId={formatDataTestId(
                      !Boolean(props.flagData.commentable)
                        ? 'Remove Flag'
                        : 'Cancel',
                      'button',
                    )}
                  />
                ))}
            </>
          ) : (
            ''
          )}
          <Button
            onClick={() => {
              setErrorValues({
                message: '',
                emailId: '',
                description: '',
              });
              handleClose();
            }}
            label="Cancel"
            dataTestId={formatDataTestId('Cancel', 'button')}
          />
        </Box>
      </>
    </BasicModal>
  );
};
